import { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import {
  BookOpen, ChevronLeft, ChevronRight, ChevronDown,
  MapPin, Clock, Palette, Users, BarChart3,
  Play, Pause,
} from 'lucide-react';
import type { BarDiagramData, ArsColorCode } from '../../types/barDiagram';
import { arsColorStyles, arsColorLabels } from './barDiagramColors';

interface Props {
  data: BarDiagramData;
  collapsed: boolean;
  onToggle: () => void;
  onActivePhaseChange?: (index: number | undefined) => void;
}

/* ── Acordeón reutilizable ── */
function SidePanelSection({
  icon: Icon,
  title,
  defaultOpen = false,
  children,
}: {
  icon: React.ElementType;
  title: string;
  defaultOpen?: boolean;
  children: React.ReactNode;
}) {
  const [open, setOpen] = useState(defaultOpen);

  return (
    <div className="border-b border-gray-800">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center gap-2 px-4 py-3 text-sm font-medium text-gray-300 hover:bg-gray-800/50 transition-colors"
      >
        <Icon className="w-4 h-4 text-gray-500 flex-shrink-0" />
        <span className="flex-1 text-left">{title}</span>
        <ChevronDown
          className={`w-4 h-4 text-gray-500 transition-transform duration-200 ${open ? '' : '-rotate-90'}`}
        />
      </button>
      {open && <div className="px-4 pb-4 text-sm text-gray-400 space-y-2">{children}</div>}
    </div>
  );
}

/* ── Clasificación de grupos ── */
type GroupKind = 'pedestrian' | 'companion' | 'special' | 'vehicular' | 'inactive';

const kindMeta: Record<GroupKind, { label: string; dot: string }> = {
  pedestrian: { label: 'Peatonal', dot: 'bg-cyan-400' },
  companion: { label: 'Compañero peatonal', dot: 'bg-purple-400' },
  special: { label: 'Vehicular especial', dot: 'bg-emerald-400' },
  vehicular: { label: 'Vehicular', dot: 'bg-green-500' },
  inactive: { label: 'Inactivo', dot: 'bg-gray-600' },
};

function classifyGroup(cells: ArsColorCode[]): GroupKind {
  const set = new Set(cells.filter(c => c !== '-'));
  if (set.size === 0) return 'inactive';
  if (set.has('p9')) return 'pedestrian';
  if (set.has('p6') || set.has('p7')) return 'companion';
  if (set.has('p10')) return 'special';
  return 'vehicular';
}

/* ── Mini-barra para fases ── */
function PhaseBar({ duration, maxDuration, type }: { duration: number; maxDuration: number; type: 'phase' | 'transition' }) {
  const pct = maxDuration > 0 ? (duration / maxDuration) * 100 : 0;
  return (
    <div className="flex items-center gap-2">
      <div
        className={`h-3 rounded-sm ${type === 'phase' ? 'bg-green-500' : 'bg-amber-500'}`}
        style={{ width: `${pct}%`, minWidth: 4 }}
      />
    </div>
  );
}

/* ── Swatch de color ── */
function ColorSwatch({ code }: { code: ArsColorCode }) {
  return (
    <span
      className="inline-block rounded-sm border border-gray-600 flex-shrink-0"
      style={{ width: 24, height: 16, ...arsColorStyles[code] }}
    />
  );
}

/* ── Barra timeline del ciclo ── */
function CycleTimelineBar({
  phases,
  totalDuration,
  progressPercent,
  activePhaseIndex,
}: {
  phases: BarDiagramData['phases'];
  totalDuration: number;
  progressPercent: number;
  activePhaseIndex: number;
}) {
  return (
    <div className="relative h-8 flex rounded overflow-hidden bg-gray-800">
      {phases.map((phase, i) => {
        const widthPct = totalDuration > 0 ? (phase.duration / totalDuration) * 100 : 0;
        return (
          <div
            key={i}
            className={`flex items-center justify-center text-[10px] font-medium transition-opacity duration-150 ${
              phase.type === 'phase'
                ? 'bg-green-600 text-green-100'
                : 'bg-amber-600 text-amber-100'
            } ${i === activePhaseIndex ? 'opacity-100' : 'opacity-60'}`}
            style={{ width: `${widthPct}%`, minWidth: widthPct > 3 ? undefined : 2 }}
            title={`${phase.label}: ${phase.duration}s`}
          >
            {widthPct > 6 ? phase.label : ''}
          </div>
        );
      })}
      {/* Indicador móvil */}
      <div
        className="absolute top-0 h-full w-0.5 bg-white shadow-[0_0_4px_rgba(255,255,255,0.8)] transition-[left] duration-100 ease-linear pointer-events-none"
        style={{ left: `${progressPercent}%` }}
      />
    </div>
  );
}

/* ── Controles play/pause + velocidad ── */
const SPEEDS = [1, 2, 3, 5] as const;

function CycleControls({
  isPlaying,
  speed,
  onTogglePlay,
  onSpeedChange,
}: {
  isPlaying: boolean;
  speed: number;
  onTogglePlay: () => void;
  onSpeedChange: (s: number) => void;
}) {
  return (
    <div className="flex items-center gap-2">
      <button
        onClick={onTogglePlay}
        className={`p-1.5 rounded transition-colors ${
          isPlaying
            ? 'bg-amber-600/30 text-amber-400 hover:bg-amber-600/50'
            : 'bg-green-600/30 text-green-400 hover:bg-green-600/50'
        }`}
        title={isPlaying ? 'Pausar' : 'Reproducir'}
      >
        {isPlaying ? <Pause className="w-3.5 h-3.5" /> : <Play className="w-3.5 h-3.5" />}
      </button>
      {SPEEDS.map(s => (
        <button
          key={s}
          onClick={() => onSpeedChange(s)}
          className={`px-1.5 py-0.5 rounded text-[11px] font-medium transition-colors ${
            speed === s
              ? 'bg-blue-600 text-white'
              : 'bg-gray-800 text-gray-500 hover:text-gray-300'
          }`}
        >
          x{s}
        </button>
      ))}
    </div>
  );
}

/* ── Componente principal ── */
export function BarDiagramSidePanel({ data, collapsed, onToggle, onActivePhaseChange }: Props) {
  const analysis = useMemo(() => {
    const usedColors = [...new Set(data.groups.flatMap(g => g.cells))]
      .filter((c): c is Exclude<ArsColorCode, '-'> => c !== '-')
      .sort();

    const groupClassification = data.groups.map(g => ({
      ...g,
      kind: classifyGroup(g.cells),
    }));

    const activeGroups = groupClassification.filter(g => g.kind !== 'inactive');

    const totalDuration = data.phases.reduce((s, p) => s + p.duration, 0);
    const phaseDuration = data.phases.filter(p => p.type === 'phase').reduce((s, p) => s + p.duration, 0);
    const transitionDuration = totalDuration - phaseDuration;
    const maxPhaseDuration = Math.max(...data.phases.map(p => p.duration), 1);

    // Tiempo de verde por grupo activo
    const greenCodes = new Set<ArsColorCode>(['p1', 'p6', 'p9', 'p10']);
    const greenPerGroup = activeGroups.map(g => {
      let greenTime = 0;
      g.cells.forEach((c, i) => {
        if (greenCodes.has(c) && data.phases[i]) {
          greenTime += data.phases[i].duration;
        }
      });
      return { label: g.label, greenTime, pct: totalDuration > 0 ? (greenTime / totalDuration) * 100 : 0 };
    });

    const offsets = data.plans.map(p => p.offset);
    const minOffset = offsets.length > 0 ? Math.min(...offsets) : 0;
    const maxOffset = offsets.length > 0 ? Math.max(...offsets) : 0;

    return {
      usedColors,
      groupClassification,
      activeGroups,
      totalDuration,
      phaseDuration,
      transitionDuration,
      maxPhaseDuration,
      greenPerGroup,
      minOffset,
      maxOffset,
    };
  }, [data]);

  /* ── Animación del ciclo ── */
  const [isPlaying, setIsPlaying] = useState(false);
  const [speed, setSpeed] = useState(1);
  const [currentTime, setCurrentTime] = useState(0);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const activePhaseIndex = useMemo(() => {
    let accum = 0;
    for (let i = 0; i < data.phases.length; i++) {
      accum += data.phases[i].duration;
      if (currentTime < accum) return i;
    }
    return data.phases.length - 1;
  }, [currentTime, data.phases]);

  const progressPercent = analysis.totalDuration > 0
    ? (currentTime / analysis.totalDuration) * 100
    : 0;

  // Timer autónomo
  useEffect(() => {
    if (!isPlaying) {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      return;
    }
    intervalRef.current = setInterval(() => {
      setCurrentTime(prev => {
        const next = prev + 0.1 * speed;
        return next >= analysis.totalDuration ? 0 : next;
      });
    }, 100);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [isPlaying, speed, analysis.totalDuration]);

  // Notificar fase activa al padre
  useEffect(() => {
    onActivePhaseChange?.(isPlaying ? activePhaseIndex : undefined);
  }, [isPlaying, activePhaseIndex, onActivePhaseChange]);

  const handleTogglePlay = useCallback(() => {
    setIsPlaying(p => !p);
  }, []);

  const handleSpeedChange = useCallback((s: number) => {
    setSpeed(s);
  }, []);

  const formatTime = (t: number) => {
    const rounded = Math.round(t * 10) / 10;
    return rounded.toFixed(1);
  };

  /* Estado colapsado: barra vertical mínima */
  if (collapsed) {
    return (
      <div className="w-10 flex-shrink-0 border-r border-gray-800 bg-gray-900/95 flex flex-col items-center pt-3">
        <button
          onClick={onToggle}
          className="p-1.5 rounded hover:bg-gray-800 text-gray-500 hover:text-gray-300 transition-colors"
          title="Abrir panel de guía"
        >
          <ChevronRight className="w-4 h-4" />
        </button>
        <div className="mt-4 flex flex-col items-center gap-1">
          <BookOpen className="w-4 h-4 text-gray-600" />
          <span
            className="text-[10px] text-gray-600 tracking-widest"
            style={{ writingMode: 'vertical-lr' }}
          >
            GUÍA
          </span>
        </div>
      </div>
    );
  }

  /* Estado expandido */
  return (
    <div className="w-80 flex-shrink-0 border-r border-gray-800 bg-gray-900/95 flex flex-col overflow-hidden transition-all duration-300">
      {/* Cabecera */}
      <div className="flex items-center gap-2 px-4 py-3 border-b border-gray-800 flex-shrink-0">
        <BookOpen className="w-4 h-4 text-blue-400" />
        <span className="text-sm font-medium text-gray-200 flex-1">Guía del Diagrama</span>
        <button
          onClick={onToggle}
          className="p-1 rounded hover:bg-gray-800 text-gray-500 hover:text-gray-300 transition-colors"
          title="Colapsar panel"
        >
          <ChevronLeft className="w-4 h-4" />
        </button>
      </div>

      {/* Secciones */}
      <div className="flex-1 overflow-y-auto">
        {/* 1. Cruce */}
        <SidePanelSection icon={MapPin} title="Cruce" defaultOpen>
          <div className="space-y-1">
            {data.metadata.cruilla && (
              <div>
                <span className="text-gray-500">Nº Cruce: </span>
                <span className="text-white font-medium">{data.metadata.cruilla}</span>
              </div>
            )}
            {data.metadata.projecte && (
              <div>
                <span className="text-gray-500">Proyecto: </span>
                <span className="text-gray-300">{data.metadata.projecte}</span>
              </div>
            )}
            {data.metadata.data && (
              <div>
                <span className="text-gray-500">Fecha: </span>
                <span className="text-gray-300">{data.metadata.data}</span>
              </div>
            )}
            {data.metadata.localitat && (
              <div>
                <span className="text-gray-500">Localidad: </span>
                <span className="text-gray-300">{data.metadata.localitat}</span>
              </div>
            )}
          </div>
        </SidePanelSection>

        {/* 2. Estructura del Ciclo */}
        <SidePanelSection icon={Clock} title="Estructura del Ciclo" defaultOpen>
          {/* Barra timeline con indicador móvil */}
          <CycleTimelineBar
            phases={data.phases}
            totalDuration={analysis.totalDuration}
            progressPercent={progressPercent}
            activePhaseIndex={activePhaseIndex}
          />

          {/* Contador + Controles */}
          <div className="flex items-center justify-between mt-2">
            <span className="text-xs text-gray-400 tabular-nums">
              {formatTime(currentTime)}s / {analysis.totalDuration}s
            </span>
            <CycleControls
              isPlaying={isPlaying}
              speed={speed}
              onTogglePlay={handleTogglePlay}
              onSpeedChange={handleSpeedChange}
            />
          </div>

          <p className="text-xs text-gray-500 mt-3">
            El ciclo semafórico se divide en <strong className="text-gray-300">fases</strong> (F)
            y <strong className="text-gray-300">transiciones</strong> (t) entre ellas.
          </p>

          {/* Lista de fases con highlight */}
          <div className="space-y-1.5">
            {data.phases.map((phase, i) => (
              <div
                key={i}
                className={`space-y-0.5 rounded px-1.5 py-0.5 transition-colors duration-150 ${
                  isPlaying && i === activePhaseIndex
                    ? 'bg-blue-500/15 ring-1 ring-blue-500/30'
                    : ''
                }`}
              >
                <div className="flex items-center justify-between text-xs">
                  <span className={phase.type === 'phase' ? 'text-green-400 font-medium' : 'text-amber-400'}>
                    {phase.label}
                  </span>
                  <span className="text-gray-500">{phase.duration}s</span>
                </div>
                <PhaseBar
                  duration={phase.duration}
                  maxDuration={analysis.maxPhaseDuration}
                  type={phase.type}
                />
              </div>
            ))}
          </div>
          <div className="mt-3 pt-2 border-t border-gray-800 text-xs">
            <div className="flex justify-between">
              <span className="text-gray-500">Ciclo total</span>
              <span className="text-white font-medium">{analysis.totalDuration}s</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Nº planes</span>
              <span className="text-gray-300">{data.plans.length}</span>
            </div>
          </div>
        </SidePanelSection>

        {/* 3. Leyenda de Colores */}
        <SidePanelSection icon={Palette} title="Leyenda de Colores">
          <p className="text-xs text-gray-500 mb-3">
            Cada celda muestra el estado de un grupo durante una fase.
          </p>
          <div className="space-y-1.5">
            {analysis.usedColors.map(code => (
              <div key={code} className="flex items-center gap-2">
                <ColorSwatch code={code} />
                <span className="text-gray-500 text-xs w-7">{code}</span>
                <span className="text-gray-300 text-xs">{arsColorLabels[code]}</span>
              </div>
            ))}
          </div>
        </SidePanelSection>

        {/* 4. Grupos Semafóricos */}
        <SidePanelSection icon={Users} title="Grupos Semafóricos">
          <p className="text-xs text-gray-500 mb-3">
            Cada fila (G1–G16) es un grupo: un conjunto de luces que cambian a la vez.
          </p>
          {(['vehicular', 'pedestrian', 'companion', 'special', 'inactive'] as GroupKind[]).map(kind => {
            const groups = analysis.groupClassification.filter(g => g.kind === kind);
            if (groups.length === 0) return null;
            const meta = kindMeta[kind];
            return (
              <div key={kind} className="mb-2">
                <div className="flex items-center gap-1.5 mb-1">
                  <span className={`w-2 h-2 rounded-full ${meta.dot}`} />
                  <span className="text-xs font-medium text-gray-300">{meta.label}</span>
                </div>
                <div className="flex flex-wrap gap-1 ml-3.5">
                  {groups.map(g => (
                    <span key={g.id} className="text-xs text-gray-500">{g.label}</span>
                  ))}
                </div>
              </div>
            );
          })}
        </SidePanelSection>

        {/* 5. Estadísticas */}
        <SidePanelSection icon={BarChart3} title="Estadísticas">
          <div className="space-y-1.5 text-xs">
            <div className="flex justify-between">
              <span className="text-gray-500">Duración total ciclo</span>
              <span className="text-white font-medium">{analysis.totalDuration}s</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Tiempo en fases</span>
              <span className="text-gray-300">
                {analysis.phaseDuration}s ({analysis.totalDuration > 0
                  ? Math.round((analysis.phaseDuration / analysis.totalDuration) * 100)
                  : 0}%)
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Tiempo en transiciones</span>
              <span className="text-gray-300">
                {analysis.transitionDuration}s ({analysis.totalDuration > 0
                  ? Math.round((analysis.transitionDuration / analysis.totalDuration) * 100)
                  : 0}%)
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Nº planes</span>
              <span className="text-gray-300">{data.plans.length}</span>
            </div>
            {data.plans.length > 0 && (
              <div className="flex justify-between">
                <span className="text-gray-500">Rango desfases</span>
                <span className="text-gray-300">
                  {analysis.minOffset === analysis.maxOffset
                    ? `${analysis.minOffset}s`
                    : `${analysis.minOffset}–${analysis.maxOffset}s`}
                </span>
              </div>
            )}

            {analysis.greenPerGroup.length > 0 && (
              <>
                <div className="mt-2 pt-2 border-t border-gray-800 text-gray-500 font-medium">
                  Verde por grupo
                </div>
                {analysis.greenPerGroup.map(g => (
                  <div key={g.label} className="flex justify-between">
                    <span className="text-gray-500">{g.label}</span>
                    <span className="text-gray-300">
                      {g.greenTime}s ({Math.round(g.pct)}%)
                    </span>
                  </div>
                ))}
              </>
            )}
          </div>
        </SidePanelSection>
      </div>
    </div>
  );
}
